// '#imports' (not 'nuxt/app'): under a link: install, 'nuxt/app' resolves to
// THIS repo's nuxt copy and mixes two Nuxt instances into the consumer build.
import { computed, useCookie, useRequestURL, useRuntimeConfig, useState } from '#imports'
import { defaultKey, pinnedKey, queryOverride, registry } from '#tenant-registry'
import { resolveTenant } from '../../../core/resolve'
import type { TenantEntry } from '../../../core/registry'
import type { EnvironmentConfig } from '../../../types/customer'

/** Dev-only tenant override. A cookie (not localStorage) so SSR dev sees it too. */
export const DEV_TENANT_COOKIE = 'tr-dev-key'

/** Per-tab home of the `?tenant=` preview override (queryOverride builds only). */
export const PREVIEW_TENANT_STORAGE = 'tr-preview-key'

// sessionStorage, not a cookie: it is scoped per tab, which is what lets one
// preview server show different tenants in different tabs at the same time,
// and it survives reloads after navigation has dropped the query string.
function previewTenantKey(): string | null {
	const fromUrl = useRequestURL().searchParams.get('tenant')
	if (typeof window === 'undefined') return fromUrl
	try {
		if (fromUrl && registry.byKey[fromUrl]) {
			window.sessionStorage.setItem(PREVIEW_TENANT_STORAGE, fromUrl)
			return fromUrl
		}
		return fromUrl ?? window.sessionStorage.getItem(PREVIEW_TENANT_STORAGE)
	} catch {
		return fromUrl
	}
}

// Guarded cast: replaced with a literal by the bundler in Nuxt contexts, and
// harmlessly undefined (falsy) anywhere else.
const isDev = (import.meta as unknown as { dev?: boolean }).dev === true

/** Shape of the shared shell's inline `window.__TENANT__` payload. */
export interface InjectedTenant {
	entry?: TenantEntry | null
	headInjected?: boolean
}

function injectedTenant(): TenantEntry | undefined {
	if (typeof window === 'undefined') return undefined
	const injected = (window as Window & { __TENANT__?: InjectedTenant }).__TENANT__
	return injected?.entry ?? undefined
}

interface TenantState {
	entry: TenantEntry | null
	source: string | null
	error: string | null
}

export function useTenant() {
	const state = useState<TenantState>('tr-tenant', () => ({
		entry: null,
		source: null,
		error: null,
	}))

	if (!state.value.entry && !state.value.error) resolveNow()

	function resolveNow() {
		let entry: TenantEntry | undefined
		let source = 'default'

		// Above injection on purpose: a preview build exists to switch tenants,
		// so the URL must beat whatever a shell injected. Real deployments never
		// ship with queryOverride on, so this rung is dead code there.
		if (queryOverride) {
			const previewKey = previewTenantKey()
			if (previewKey && registry.byKey[previewKey]) {
				entry = registry.byKey[previewKey]
				source = 'query'
			}
		}

		// Ahead of everything but the preview override: a shared server deployment
		// resolved the tenant from the Host already (with clientPayload: 'server'
		// the client registry is empty, so the injected entry is the only truth).
		const injected = injectedTenant()
		if (!entry && injected) {
			entry = injected
			source = 'injected'
		}
		if (!entry && isDev) {
			const override = useCookie<string | null>(DEV_TENANT_COOKIE).value
			if (override && registry.byKey[override]) {
				entry = registry.byKey[override]
				source = 'devOverride'
			}
		}
		if (!entry && pinnedKey) {
			entry = registry.byKey[pinnedKey]
			source = 'pinned'
		}
		if (!entry) {
			const runtimeKey = String(useRuntimeConfig().public.tenantKey ?? '')
			if (runtimeKey) {
				entry = resolveTenant(registry, { key: runtimeKey, host: runtimeKey })
				source = 'runtimeKey'
			}
		}
		if (!entry) {
			const host = useRequestURL().host
			entry = resolveTenant(registry, { host })
			source = 'host'
		}
		if (!entry && defaultKey) {
			entry = registry.byKey[defaultKey]
			source = 'defaultKey'
		}

		if (entry) {
			state.value = { entry, source, error: null }
		} else {
			// An empty registry means clientPayload: 'server': resolution happens on
			// the server, so a missing injection is the failure worth naming.
			state.value = {
				entry: null,
				source: null,
				error: registry.entries.length
					? `No tenant resolved for host "${useRequestURL().host}". Registered keys: ${registry.keys().join(', ')}`
					: 'No tenant was injected by the server (window.__TENANT__ is empty) and this bundle ships no registry.',
			}
		}
	}

	const entry = computed(() => state.value.entry)
	const customer = computed(() => state.value.entry?.customer ?? null)
	const deployment = computed(() => state.value.entry?.deployment ?? null)
	const brand = computed(() => state.value.entry?.customer.brand ?? null)
	const displayName = computed(
		() => state.value.entry?.deployment.displayName ?? state.value.entry?.customer.name ?? '',
	)

	/** The resolved `EnvironmentConfig` this deployment names. */
	const environment = computed<EnvironmentConfig | null>(() => {
		const current = state.value.entry
		if (!current) return null
		return current.customer.environments?.[current.deployment.environment] ?? null
	})

	const endpoints = computed(() => environment.value?.endpoints ?? null)
	const apiOrigin = computed(() => environment.value?.endpoints.api ?? '')
	const observability = computed(() => environment.value?.observability ?? null)

	/**
	 * The registry's two settings layers, unmerged. Each app composes these under
	 * its own defaults and preset; this package deliberately does not know what
	 * any of the keys mean.
	 */
	const settings = computed(() => ({
		customer: state.value.entry?.customer.settings ?? undefined,
		deployment: state.value.entry?.deployment.settings ?? undefined,
	}))

	const error = computed(() => state.value.error)
	const source = computed(() => state.value.source)

	return {
		entry,
		customer,
		deployment,
		brand,
		displayName,
		environment,
		endpoints,
		apiOrigin,
		observability,
		settings,
		error,
		source,
		resolve: resolveNow,
	}
}
